import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useChangeAppointmentStatusMutation } from "../../api/Appointments/changeAppointmentStatus";
import { refetchAllAppointmentsApis } from "../../api/Appointments/refetchAllAppointmentsApis";
import { useToast } from "../../Services/ToastContext";
import AppointmentActionBtn from "../Btn/AppointmentActionBtn"; 
import { AppointmentCancelConfirm } from "../../pages/Appointments/component";

interface AppointmentActionsProps {
  action: "reject" | "confirm";
  appointmentId: number | string;
  stopPropagation?: boolean;
}

const AppointmentActions: React.FC<AppointmentActionsProps> = ({
  action,
  appointmentId,
  stopPropagation = true,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [confirmCancelStatus, setConfirmCancelStatus] = useState<
    "Reject" | "Cancel"
  >();

  const { mutate, isPending } = useChangeAppointmentStatusMutation({
    onSuccess: () => {
      setIsCancelConfirmOpen(false);
      if (
        confirmCancelStatus === "Reject" ||
        confirmCancelStatus === "Cancel"
      ) {
        showToast(
          t(
            confirmCancelStatus === "Reject"
              ? "appointment_rejected_successfully"
              : "appointment_canceled_successfully",
          ),
          "success",
        );
        setConfirmCancelStatus(undefined);
      } else showToast(t("appointment_approved_successfully"), "success");
    },
    onError: (error) => {
      showToast(error.message, "error");
    },
  });

  const onSubmit = (values: { Note: string }) => {
    mutate(
      {
        AppointmentId: appointmentId,
        action: confirmCancelStatus,
        Note: values?.Note,
      } as any,
      {
        onSuccess: () => {
          setIsCancelConfirmOpen(false);
          refetchAllAppointmentsApis(queryClient);
        },
        onError: (error) => {
          showToast(error.message, "error");
        },
      },
    );
  };

  const handleReject = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    setIsCancelConfirmOpen(true);
    setConfirmCancelStatus("Reject");
  };

  const handleConfirm = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    mutate({
      AppointmentId: appointmentId,
      action: "Approve",
    } as any);
  };

  if (action === "reject") {
    return (
      <>
        <AppointmentActionBtn action="reject" onClick={handleReject} />
        {isCancelConfirmOpen && (
          <AppointmentCancelConfirm
            onSubmit={onSubmit}
            isLoading={isPending}
            status={confirmCancelStatus}
            isOpen={isCancelConfirmOpen}
            onClose={() => setIsCancelConfirmOpen(false)}
          />
        )}
      </>
    );
  }

  return <AppointmentActionBtn action="confirm" onClick={handleConfirm} />;
};

export default AppointmentActions;
